import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  Calculator, Search, X, TrendingUp, AlertCircle,
  ArrowRight, BookOpen, Sparkles,
} from 'lucide-react';
import BackOrHomeButton from '@/components/BackOrHomeButton';
import SiteDisclaimer from '@/components/SiteDisclaimer';
import { useSeoMeta } from '@/pages/learn/LearnPage';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Dividend Reinvestment Calculator — public SEO landing (Feb 2026).
 *
 * Route: /dividend-reinvestment-calculator
 *
 * Standalone version of the Stock-Modal DRIP calculator, targeted at the
 * raw keyword "dividend reinvestment calculator". Reads directly from
 * stock_cache (populated by Massive API — no yfinance) so Googlebot
 * safe. Falls back to a manual mode where the user enters yield + price
 * without picking a ticker.
 *
 * Math model MATCHES the Stock Modal DRIP tab exactly:
 *   • 5% constant annual price growth
 *   • 15% default tax throttle
 *   • Dividend growth: raw 5yr CAGR ÷ 10, capped at 13%/yr
 *   • Yield cap: 20% (mirrors the Portfolio DRIP TRAP_CAP) — anything
 *     higher is clamped to prevent YieldMax/JEPY-style distortion
 *   • Annual compounding, DRIP at post-growth start-of-year price
 */

const HORIZONS = [
  { years: 5, label: '5 years' },
  { years: 10, label: '10 years' },
  { years: 20, label: '20 years' },
];
const PRICE_GROWTH = 0.05;
const DEFAULT_TAX_RATE = 15;
const YIELD_CAP_PCT = 20;
const GROWTH_CAP_PCT = 13;

export default function DividendReinvestmentCalculator() {
  useSeoMeta({
    title: 'Dividend Reinvestment Calculator (DRIP) — Free & Accurate | DiviDrip',
    description: 'Free dividend reinvestment (DRIP) calculator. Pick any of 5,350+ dividend stocks or enter your own yield. See projected shares, portfolio value, and dividend income over 5, 10, or 20 years.',
    canonical: 'https://twylightcrow.com/dividend-reinvestment-calculator',
  });

  // ── Inputs ────────────────────────────────────────────────────
  const [mode, setMode] = useState('ticker'); // 'ticker' | 'manual'
  const [ticker, setTicker] = useState(null); // { ticker, name, price, dividend_yield, dividend_rate }
  const [monthlyInvest, setMonthlyInvest] = useState(200);
  const [initialShares, setInitialShares] = useState(0);
  const [horizon, setHorizon] = useState(20);
  const [taxRate, setTaxRate] = useState(DEFAULT_TAX_RATE);
  const [divGrowthRate, setDivGrowthRate] = useState(3); // conservative default

  // Manual-mode inputs
  const [manualPrice, setManualPrice] = useState(50);
  const [manualYield, setManualYield] = useState(4);   // % form
  const [manualName, setManualName] = useState('');

  // Load dividend growth CAGR when a ticker is picked (matches DripCalcTab)
  useEffect(() => {
    if (mode !== 'ticker' || !ticker?.ticker) return;
    let cancelled = false;
    axios.get(`${API}/stocks/${ticker.ticker}/dividend-growth`, { withCredentials: true })
      .then((res) => {
        if (cancelled) return;
        const cagr = res.data?.cagr_5yr;
        if (Number.isFinite(cagr)) {
          const damped = cagr / 10;
          setDivGrowthRate(Math.min(GROWTH_CAP_PCT, damped));
        } else {
          setDivGrowthRate(1);
        }
      })
      .catch(() => { if (!cancelled) setDivGrowthRate(1); });
    return () => { cancelled = true; };
  }, [mode, ticker?.ticker]);

  // ── Effective values (with caps applied) ─────────────────────
  const eff = useMemo(() => {
    if (mode === 'manual') {
      const rawYield = Number(manualYield) || 0;
      const cappedYield = Math.min(YIELD_CAP_PCT, Math.max(0, rawYield));
      const price = Math.max(0.01, Number(manualPrice) || 0);
      return {
        name: manualName.trim() || 'Custom stock',
        ticker: null,
        price,
        yieldPct: cappedYield,
        annualDiv: price * (cappedYield / 100),
        yieldWasCapped: rawYield > YIELD_CAP_PCT,
      };
    }
    if (!ticker) return null;
    const rawYield = Number(ticker.dividend_yield) || 0;
    const cappedYield = Math.min(YIELD_CAP_PCT, Math.max(0, rawYield));
    const price = Math.max(0.01, Number(ticker.price) || 0);
    // Prefer the actual dividend_rate; fall back to yield×price if missing.
    const annualDiv = Number(ticker.dividend_rate) > 0
      ? Number(ticker.dividend_rate) * (cappedYield / rawYield || 1)
      : price * (cappedYield / 100);
    return {
      name: ticker.name || ticker.ticker,
      ticker: ticker.ticker,
      price,
      yieldPct: cappedYield,
      annualDiv,
      yieldWasCapped: rawYield > YIELD_CAP_PCT,
    };
  }, [mode, ticker, manualPrice, manualYield, manualName]);

  // ── DRIP projection — matches DripCalcTab exactly ────────────
  const results = useMemo(() => {
    if (!eff || !eff.price || !eff.annualDiv) return null;
    const price0 = eff.price;
    const annualDiv0 = eff.annualDiv;
    const growthCapped = Math.min(GROWTH_CAP_PCT, Number(divGrowthRate) || 0);
    const divGrowthSafe = Math.max(-0.99, growthCapped / 100);
    const monthly = Math.max(0, Number(monthlyInvest) || 0);
    const startShares = Math.max(0, Number(initialShares) || 0);
    const baselineCost = startShares * price0;
    const taxMult = 1 - (Math.max(0, Math.min(100, Number(taxRate) || 0)) / 100);

    let totalShares = startShares;
    let currentPrice = price0;
    let totalInvested = baselineCost;
    const yearlyData = [];

    for (let year = 1; year <= horizon; year++) {
      const currentAnnualDiv = annualDiv0 * Math.pow(1 + divGrowthSafe, year - 1);
      if (totalShares > 0) {
        const divPayment = totalShares * currentAnnualDiv * taxMult;
        totalShares += divPayment / currentPrice;
      }
      currentPrice *= (1 + PRICE_GROWTH);
      if (monthly > 0) {
        const annualContrib = monthly * 12;
        totalShares += annualContrib / currentPrice;
        totalInvested += annualContrib;
      }
      const portfolioValue = totalShares * currentPrice;
      const annualDividends = totalShares * currentAnnualDiv;
      yearlyData.push({
        year,
        portfolioValue: Math.round(portfolioValue),
        totalInvested: Math.round(totalInvested),
        annualDividends: Math.round(annualDividends),
        totalShares: parseFloat(totalShares.toFixed(2)),
      });
    }

    const final = yearlyData[yearlyData.length - 1];
    const totalReturnPct = final.totalInvested > 0
      ? ((final.portfolioValue - final.totalInvested) / final.totalInvested * 100)
      : 0;

    return {
      yearlyData,
      final,
      totalReturnPct,
      growthCapped: growthCapped < Number(divGrowthRate),
    };
  }, [eff, monthlyInvest, initialShares, horizon, taxRate, divGrowthRate]);

  return (
    <div className="max-w-5xl mx-auto px-4 py-8 sm:py-12" data-testid="drip-calc-page">
      <BackOrHomeButton />

      {/* Breadcrumb */}
      <nav aria-label="Breadcrumb" className="mt-4 text-xs text-slate-500 dark:text-slate-400" data-testid="drip-calc-breadcrumb">
        <Link to="/" className="hover:text-cyan-600 dark:hover:text-cyan-400">Home</Link>
        <span className="mx-2 opacity-50">/</span>
        <span className="text-slate-700 dark:text-slate-300">Dividend Reinvestment Calculator</span>
      </nav>

      {/* Hero */}
      <div className="mt-4 flex items-start gap-4">
        <div className="hidden sm:flex w-12 h-12 rounded-xl bg-cyan-100 dark:bg-cyan-950/40 border border-cyan-200 dark:border-cyan-800/50 items-center justify-center shrink-0">
          <Calculator className="w-6 h-6 text-cyan-600 dark:text-cyan-400" strokeWidth={2.2} />
        </div>
        <div>
          <h1 className="text-3xl sm:text-4xl font-black text-slate-900 dark:text-slate-100 leading-tight" data-testid="drip-calc-h1">
            Dividend Reinvestment Calculator
          </h1>
          <p className="mt-2 text-sm sm:text-base text-slate-600 dark:text-slate-400 max-w-2xl">
            Project your portfolio value and dividend income when you reinvest every dividend (DRIP). Pick any of 5,350+ tracked tickers or run a custom scenario.
          </p>
        </div>
      </div>

      {/* Mode toggle */}
      <div className="mt-6 inline-flex rounded-lg border border-slate-200 dark:border-slate-800 p-1 bg-slate-50 dark:bg-slate-900" data-testid="drip-calc-mode-toggle">
        <button
          type="button"
          className={`px-4 py-1.5 text-sm font-semibold rounded transition-colors ${
            mode === 'ticker'
              ? 'bg-cyan-600 text-white'
              : 'text-slate-700 dark:text-slate-300 hover:text-slate-900'
          }`}
          onClick={() => setMode('ticker')}
          data-testid="drip-calc-mode-ticker"
        >
          Pick a stock
        </button>
        <button
          type="button"
          className={`px-4 py-1.5 text-sm font-semibold rounded transition-colors ${
            mode === 'manual'
              ? 'bg-cyan-600 text-white'
              : 'text-slate-700 dark:text-slate-300 hover:text-slate-900'
          }`}
          onClick={() => setMode('manual')}
          data-testid="drip-calc-mode-manual"
        >
          Custom yield &amp; price
        </button>
      </div>

      {/* Input grid */}
      <div className="mt-4 grid md:grid-cols-2 gap-6">
        {/* Left column: Stock/Custom input */}
        <div className="p-5 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950/50" data-testid="drip-calc-input-left">
          {mode === 'ticker' ? (
            <TickerPicker value={ticker} onChange={setTicker} />
          ) : (
            <ManualInputs
              name={manualName}
              price={manualPrice}
              yieldPct={manualYield}
              onName={setManualName}
              onPrice={setManualPrice}
              onYield={setManualYield}
              yieldCapped={eff?.yieldWasCapped}
            />
          )}
        </div>

        {/* Right column: DRIP inputs */}
        <div className="p-5 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950/50" data-testid="drip-calc-input-right">
          <div className="text-xs uppercase tracking-wider font-bold text-slate-500 dark:text-slate-400 mb-3">Reinvestment plan</div>
          <div className="space-y-3">
            <NumberInput
              label="Monthly contribution"
              value={monthlyInvest}
              onChange={setMonthlyInvest}
              prefix="$"
              testId="drip-calc-monthly"
            />
            <NumberInput
              label="Starting shares owned"
              value={initialShares}
              onChange={setInitialShares}
              testId="drip-calc-starting-shares"
            />
            <div>
              <label className="text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1 block">Horizon</label>
              <div className="grid grid-cols-3 gap-2" data-testid="drip-calc-horizon">
                {HORIZONS.map((h) => (
                  <button
                    key={h.years}
                    type="button"
                    className={`px-3 py-2 text-sm font-semibold rounded-lg border transition-colors ${
                      horizon === h.years
                        ? 'bg-cyan-600 text-white border-cyan-600'
                        : 'bg-white dark:bg-slate-950/50 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:border-cyan-400'
                    }`}
                    onClick={() => setHorizon(h.years)}
                    data-testid={`drip-calc-horizon-${h.years}`}
                  >
                    {h.label}
                  </button>
                ))}
              </div>
            </div>
            <NumberInput
              label="Tax rate on dividends"
              value={taxRate}
              onChange={setTaxRate}
              suffix="%"
              testId="drip-calc-tax"
            />
            <div>
              <NumberInput
                label={`Dividend growth per year${results?.growthCapped ? ` (capped at ${GROWTH_CAP_PCT}%)` : ''}`}
                value={divGrowthRate}
                onChange={setDivGrowthRate}
                suffix="%"
                testId="drip-calc-growth"
              />
              <p className="text-[10px] mt-1 text-slate-500 dark:text-slate-500">
                Auto-loaded from 5-year dividend CAGR ÷ 10 (matches Stock Modal). Capped at {GROWTH_CAP_PCT}%/yr — no stock sustains higher long-term.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Yield-cap warning */}
      {eff?.yieldWasCapped && (
        <div className="mt-4 p-3 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800/50 text-sm text-amber-800 dark:text-amber-300 flex items-start gap-2" data-testid="drip-calc-yield-cap-warning">
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>
            Yield capped at <strong>{YIELD_CAP_PCT}%</strong> for realistic projection. Yields above {YIELD_CAP_PCT}% almost never sustain — most high-payout ETFs like YieldMax and JEPY erode NAV to fund the distribution. Same cap the DiviDrip Portfolio Calculator uses.
          </span>
        </div>
      )}

      {/* Results */}
      {results ? (
        <ResultsBlock
          eff={eff}
          horizon={horizon}
          results={results}
          data-testid="drip-calc-results"
        />
      ) : (
        <div className="mt-6 p-8 rounded-xl border-2 border-dashed border-slate-200 dark:border-slate-800 text-center text-slate-500 dark:text-slate-400" data-testid="drip-calc-empty">
          {mode === 'ticker' ? 'Pick a ticker above to see your projection.' : 'Enter a price and yield to see your projection.'}
        </div>
      )}

      {/* Educational content — SEO fuel */}
      <EducationalBlock />

      {/* FAQ + JSON-LD */}
      <FaqBlock />

      <SiteDisclaimer />
    </div>
  );
}

/* ─── Ticker picker ────────────────────────────────────────────── */

function TickerPicker({ value, onChange }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!q || q.length < 1 || value?.ticker === q.toUpperCase()) {
      setResults([]);
      return;
    }
    debounceRef.current = setTimeout(() => {
      setIsSearching(true);
      axios.get(`${API}/stocks/search`, { params: { q } })
        .then((r) => {
          const rows = Array.isArray(r.data) ? r.data : (r.data?.results || []);
          setResults(rows.slice(0, 8));
        })
        .catch(() => setResults([]))
        .finally(() => setIsSearching(false));
    }, 200);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [q, value?.ticker]);

  return (
    <>
      <div className="text-xs uppercase tracking-wider font-bold text-slate-500 dark:text-slate-400 mb-3">
        Pick a dividend stock
      </div>
      {value ? (
        <div className="p-3 rounded-lg bg-cyan-50 dark:bg-cyan-950/30 border border-cyan-200 dark:border-cyan-800/50 flex items-start justify-between gap-3" data-testid="drip-calc-selected-ticker">
          <div className="min-w-0">
            <div className="flex items-baseline gap-2">
              <span className="text-lg font-black font-mono text-cyan-700 dark:text-cyan-300">{value.ticker}</span>
              <span className="text-xs text-slate-500 truncate">{value.name}</span>
            </div>
            <div className="mt-1 text-sm font-mono text-slate-700 dark:text-slate-300">
              ${Number(value.price || 0).toFixed(2)} · Yield <strong>{Number(value.dividend_yield || 0).toFixed(2)}%</strong>
              {value.dividend_rate ? <> · ${Number(value.dividend_rate).toFixed(2)}/yr</> : null}
            </div>
          </div>
          <button
            type="button"
            className="p-1 rounded hover:bg-cyan-100 dark:hover:bg-cyan-900/50 text-slate-500 hover:text-slate-700 shrink-0"
            onClick={() => { onChange(null); setQ(''); }}
            aria-label="Clear selection"
            data-testid="drip-calc-clear-ticker"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <div className="relative">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value.toUpperCase())}
              placeholder="Search ticker (SCHD, JEPI, O...)"
              className="w-full pl-9 pr-3 py-2.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950 text-slate-900 dark:text-slate-100 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
              data-testid="drip-calc-ticker-search"
              autoComplete="off"
            />
          </div>
          {results.length > 0 && (
            <div className="mt-2 border border-slate-200 dark:border-slate-800 rounded-lg overflow-hidden bg-white dark:bg-slate-950" data-testid="drip-calc-ticker-results">
              {results.map((r) => (
                <button
                  key={r.ticker}
                  type="button"
                  className="w-full flex items-center justify-between px-3 py-2 hover:bg-slate-50 dark:hover:bg-slate-900 text-left text-sm border-b border-slate-100 dark:border-slate-900 last:border-b-0"
                  onClick={() => { onChange(r); setQ(''); setResults([]); }}
                  data-testid={`drip-calc-ticker-result-${r.ticker}`}
                >
                  <div className="min-w-0">
                    <span className="font-mono font-bold text-slate-900 dark:text-slate-100">{r.ticker}</span>
                    <span className="ml-2 text-xs text-slate-500 truncate">{r.name}</span>
                  </div>
                  <span className="text-xs font-mono text-emerald-600 dark:text-emerald-400 shrink-0">
                    {r.dividend_yield != null ? `${Number(r.dividend_yield).toFixed(2)}%` : '—'}
                  </span>
                </button>
              ))}
            </div>
          )}
          {q.length >= 1 && !isSearching && results.length === 0 && (
            <div className="mt-2 text-xs text-slate-500">No matches — try a different symbol.</div>
          )}
        </div>
      )}
    </>
  );
}

/* ─── Manual mode ──────────────────────────────────────────────── */

function ManualInputs({ name, price, yieldPct, onName, onPrice, onYield, yieldCapped }) {
  return (
    <>
      <div className="text-xs uppercase tracking-wider font-bold text-slate-500 dark:text-slate-400 mb-3">
        Custom scenario
      </div>
      <div className="space-y-3">
        <div>
          <label className="text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1 block">Name (optional)</label>
          <input
            type="text"
            value={name}
            onChange={(e) => onName(e.target.value)}
            placeholder="e.g. My hypothetical dividend stock"
            className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
            data-testid="drip-calc-manual-name"
          />
        </div>
        <NumberInput label="Share price" value={price} onChange={onPrice} prefix="$" testId="drip-calc-manual-price" />
        <NumberInput label={`Dividend yield${yieldCapped ? ` (capped at ${YIELD_CAP_PCT}%)` : ''}`} value={yieldPct} onChange={onYield} suffix="%" testId="drip-calc-manual-yield" />
        <p className="text-[10px] text-slate-500 dark:text-slate-500 leading-relaxed">
          Custom mode is for "what-if" scenarios. For actual DRIP projections on tracked tickers, switch to "Pick a stock" so we can use the live 5-year dividend growth rate.
        </p>
      </div>
    </>
  );
}

/* ─── Number input ─────────────────────────────────────────────── */

function NumberInput({ label, value, onChange, prefix, suffix, testId }) {
  return (
    <div>
      <label className="text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1 block">{label}</label>
      <div className="relative">
        {prefix && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">{prefix}</span>}
        <input
          type="number"
          value={value}
          onChange={(e) => onChange(e.target.value === '' ? '' : Number(e.target.value))}
          className={`w-full py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950 text-slate-900 dark:text-slate-100 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 ${prefix ? 'pl-7' : 'pl-3'} ${suffix ? 'pr-9' : 'pr-3'}`}
          onPaste={(e) => {
            // Match the numericInputUtils blockNonNumericPaste behaviour
            // — just strip everything but digits, dot, minus.
            const t = (e.clipboardData || window.clipboardData).getData('text');
            if (!/^-?\d*\.?\d*$/.test(t)) { e.preventDefault(); }
          }}
          data-testid={testId}
        />
        {suffix && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">{suffix}</span>}
      </div>
    </div>
  );
}

/* ─── Results block ────────────────────────────────────────────── */

function ResultsBlock({ eff, horizon, results }) {
  const { yearlyData, final, totalReturnPct } = results;
  return (
    <div className="mt-6" data-testid="drip-calc-results">
      <div className="flex items-baseline justify-between gap-4 mb-4">
        <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100">
          After <span className="text-cyan-600 dark:text-cyan-400">{horizon} years</span> reinvesting {eff.name}
        </h2>
        <span className="text-xs text-slate-500 dark:text-slate-400 font-mono">
          {eff.yieldPct.toFixed(2)}% yield @ ${eff.price.toFixed(2)}
        </span>
      </div>

      {/* Result cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <ResultCard label="Portfolio value" value={`$${final.portfolioValue.toLocaleString()}`} accent="cyan" testId="drip-calc-result-value" />
        <ResultCard label="Total invested" value={`$${final.totalInvested.toLocaleString()}`} testId="drip-calc-result-invested" />
        <ResultCard
          label="Total return"
          value={`${totalReturnPct >= 0 ? '+' : ''}${totalReturnPct.toFixed(1)}%`}
          accent={totalReturnPct >= 0 ? 'green' : 'red'}
          testId="drip-calc-result-return"
        />
        <ResultCard label={`Annual dividends (Y${horizon})`} value={`$${final.annualDividends.toLocaleString()}`} accent="green" testId="drip-calc-result-annualdiv" />
      </div>

      <div className="grid grid-cols-2 gap-3 mt-3">
        <ResultCard label="Total shares" value={final.totalShares.toLocaleString()} testId="drip-calc-result-shares" />
        <ResultCard label="Yield on cost" value={final.totalInvested > 0 ? `${((final.annualDividends / final.totalInvested) * 100).toFixed(2)}%` : '—'} accent="green" testId="drip-calc-result-yoc" />
      </div>

      {/* Bar chart */}
      <MiniChart yearlyData={yearlyData} />

      {/* Year-by-year table (collapsed by default) */}
      <details className="mt-4 p-4 rounded-lg border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/50" data-testid="drip-calc-year-table-details">
        <summary className="cursor-pointer text-sm font-semibold text-slate-700 dark:text-slate-300 flex items-center gap-2">
          <Sparkles className="w-4 h-4" /> Year-by-year breakdown
        </summary>
        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-xs font-mono">
            <thead className="text-slate-500 dark:text-slate-400">
              <tr>
                <th className="text-left py-1 pr-2">Year</th>
                <th className="text-right py-1 px-2">Portfolio</th>
                <th className="text-right py-1 px-2">Invested</th>
                <th className="text-right py-1 px-2">Div income</th>
                <th className="text-right py-1 px-2">Shares</th>
              </tr>
            </thead>
            <tbody>
              {yearlyData.map((d) => (
                <tr key={d.year} className="border-t border-slate-200 dark:border-slate-800">
                  <td className="py-1 pr-2 font-semibold">Y{d.year}</td>
                  <td className="text-right py-1 px-2">${d.portfolioValue.toLocaleString()}</td>
                  <td className="text-right py-1 px-2">${d.totalInvested.toLocaleString()}</td>
                  <td className="text-right py-1 px-2 text-emerald-600 dark:text-emerald-400">${d.annualDividends.toLocaleString()}</td>
                  <td className="text-right py-1 px-2">{d.totalShares.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>

      <p className="mt-3 text-[10px] text-slate-500 dark:text-slate-500 leading-relaxed">
        Model uses 5% annual price growth, annual compounding, and after-tax dividend reinvestment. Assumes yields stay stable and DRIPs occur at start-of-year prices. Real market returns vary — treat this as a directional planning tool, not a prediction.
      </p>
    </div>
  );
}

function ResultCard({ label, value, accent, testId }) {
  const accentColor = accent === 'green'
    ? 'text-emerald-600 dark:text-emerald-400'
    : accent === 'cyan'
      ? 'text-cyan-600 dark:text-cyan-400'
      : accent === 'red'
        ? 'text-rose-600 dark:text-rose-400'
        : 'text-slate-900 dark:text-slate-100';
  return (
    <div className="p-3 rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950/50" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 dark:text-slate-400">{label}</div>
      <div className={`mt-1 text-lg font-bold font-mono ${accentColor}`}>{value}</div>
    </div>
  );
}

/* ─── Mini bar chart ───────────────────────────────────────────── */

function MiniChart({ yearlyData }) {
  if (!yearlyData || yearlyData.length === 0) return null;
  const maxVal = Math.max(...yearlyData.map((d) => Math.max(d.portfolioValue, d.totalInvested)));
  const ceiling = Math.max(1, Math.ceil(maxVal / 10000) * 10000);
  const yLabels = [];
  for (let i = 4; i >= 0; i--) yLabels.push(`$${((ceiling * i / 4) / 1000).toFixed(0)}k`);
  return (
    <div className="mt-4 p-4 rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950/50" data-testid="drip-calc-chart">
      <div className="text-xs font-bold text-slate-700 dark:text-slate-300 mb-2">Portfolio value vs total invested</div>
      <div className="flex items-end gap-0">
        <div className="flex flex-col justify-between h-40 pr-2 shrink-0">
          {yLabels.map((label, i) => <span key={i} className="text-[9px] font-mono text-slate-400">{label}</span>)}
        </div>
        <div className="flex-1 flex items-end gap-1 h-40 border-b border-l border-slate-200 dark:border-slate-800 pl-1">
          {yearlyData.map((d) => {
            const pv = ceiling > 0 ? (d.portfolioValue / ceiling) * 100 : 0;
            const ti = ceiling > 0 ? (d.totalInvested / ceiling) * 100 : 0;
            return (
              <div key={d.year} className="flex-1 flex items-end justify-center gap-[2px] h-full">
                <div className="w-[45%] max-w-[24px] rounded-t-sm bg-cyan-500" style={{ height: `${Math.max(pv, 1)}%` }} title={`Y${d.year}: $${d.portfolioValue.toLocaleString()}`} />
                <div className="w-[45%] max-w-[24px] rounded-t-sm bg-slate-300 dark:bg-slate-600" style={{ height: `${Math.max(ti, 1)}%` }} title={`Y${d.year} invested: $${d.totalInvested.toLocaleString()}`} />
              </div>
            );
          })}
        </div>
      </div>
      <div className="flex items-center justify-center gap-4 mt-2 text-[10px] font-bold text-slate-500">
        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 bg-cyan-500 rounded-sm inline-block" /> Portfolio</span>
        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 bg-slate-300 dark:bg-slate-600 rounded-sm inline-block" /> Invested</span>
      </div>
    </div>
  );
}

/* ─── Educational content ──────────────────────────────────────── */

function EducationalBlock() {
  return (
    <div className="mt-10 grid md:grid-cols-2 gap-6" data-testid="drip-calc-education">
      <div className="p-5 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/50">
        <h2 className="text-lg font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2 mb-2">
          <TrendingUp className="w-5 h-5 text-cyan-600 dark:text-cyan-400" /> How DRIP compounds
        </h2>
        <p className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed">
          A Dividend Reinvestment Plan (DRIP) takes every dividend payment and immediately buys more shares of the same stock — no commission, no fractional-share hassle. Those new shares earn their own dividends, which buy more shares. The math works because you&apos;re growing the share count faster than the yield can decay.
        </p>
        <Link to="/learn/what-is-drip" className="inline-flex items-center gap-1 mt-3 text-sm text-cyan-600 dark:text-cyan-400 hover:underline" data-testid="drip-calc-edu-what">
          <BookOpen className="w-3.5 h-3.5" /> Full DRIP guide
        </Link>
      </div>
      <div className="p-5 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/50">
        <h2 className="text-lg font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2 mb-2">
          <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-400" /> Why we cap yield at {YIELD_CAP_PCT}%
        </h2>
        <p className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed">
          Yields above {YIELD_CAP_PCT}% are almost always a mirage — YieldMax and JEPY-style ETFs erode NAV to fund the distribution, so the "yield" is really return-of-capital in disguise. Projecting a 40% yield forward compounds unrealistically. Same cap the DiviDrip Portfolio Calculator uses so your public projection matches the one behind the login.
        </p>
        <Link to="/learn/dividend-traps" className="inline-flex items-center gap-1 mt-3 text-sm text-cyan-600 dark:text-cyan-400 hover:underline" data-testid="drip-calc-edu-traps">
          <BookOpen className="w-3.5 h-3.5" /> Yield-trap guide
        </Link>
      </div>
      <div className="p-5 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/50">
        <h2 className="text-lg font-bold text-slate-900 dark:text-slate-100 mb-2">Taxes matter</h2>
        <p className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed">
          The tax rate on this page throttles how much each dividend reinvests. Default 15% matches the qualified-dividend rate for most middle-bracket US investors. Set to 0% if you&apos;re running the math for a Roth IRA or 401(k).
        </p>
        <Link to="/learn/qualified-vs-ordinary-dividends" className="inline-flex items-center gap-1 mt-3 text-sm text-cyan-600 dark:text-cyan-400 hover:underline" data-testid="drip-calc-edu-tax">
          <BookOpen className="w-3.5 h-3.5" /> Qualified vs ordinary dividends
        </Link>
      </div>
      <div className="p-5 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/50">
        <h2 className="text-lg font-bold text-slate-900 dark:text-slate-100 mb-2">Growth rate: raw CAGR ÷ 10</h2>
        <p className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed">
          When you pick a ticker, the calculator auto-loads that stock&apos;s 5-year dividend CAGR and divides it by 10 — because a stock that grew its dividend 42% over 5 years almost never sustains 42%/yr forward. Damping by 10 lands at 4.2%/yr, which historically better matches decade-long realized growth. Hard capped at {GROWTH_CAP_PCT}%/yr for the same reason.
        </p>
        <Link to="/learn/dividend-growth-rate" className="inline-flex items-center gap-1 mt-3 text-sm text-cyan-600 dark:text-cyan-400 hover:underline" data-testid="drip-calc-edu-growth">
          <BookOpen className="w-3.5 h-3.5" /> Dividend growth explained
        </Link>
      </div>
    </div>
  );
}

/* ─── FAQ + JSON-LD ────────────────────────────────────────────── */

const FAQS = [
  {
    q: 'What is a dividend reinvestment calculator?',
    a: 'A dividend reinvestment (DRIP) calculator projects how a stock position grows over time when you automatically use every dividend payment to buy more shares. The compounding effect can be dramatic over 10 to 20 years, especially for tickers with modest yield growth.',
  },
  {
    q: 'Is this DRIP calculator free?',
    a: 'Yes — 100% free, no sign-up, no paywall. It uses the same math model as the DiviDrip in-app Stock Modal calculator. Sign in only if you want to run projections across your full portfolio at once.',
  },
  {
    q: 'How accurate is the projection?',
    a: 'The model assumes constant 5% price growth, stable yield, and a hard 20% yield ceiling to prevent unrealistic projections. Actual returns depend on market conditions, dividend cuts or raises, and tax treatment. Treat the output as directional, not a forecast.',
  },
  {
    q: 'Why is the dividend growth rate capped at 13%?',
    a: 'No dividend stock sustains growth above 13% per year over the long term. Even Dividend Aristocrats with strong recent growth tend to converge on 6-10% annualized over 10+ years. Capping keeps projections honest — the same cap the DiviDrip Portfolio Calculator uses.',
  },
  {
    q: 'Does DRIP work in a Roth IRA?',
    a: 'Yes, and it works exceptionally well — dividends inside a Roth are never taxed, so 100% of every payout reinvests. Set the tax rate to 0% in the calculator to match Roth or 401(k) treatment.',
  },
  {
    q: 'What about YieldMax / JEPY / covered-call ETFs?',
    a: 'These pay huge headline yields but the payouts are largely return-of-capital that erodes NAV. Projecting them forward at 40-60% "yield" would be misleading, which is why we cap yield input at 20%. The Learn guide on covered-call ETFs walks through why.',
  },
];

function FaqBlock() {
  useEffect(() => {
    const jsonLd = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: FAQS.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    };
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(jsonLd);
    script.setAttribute('data-testid', 'drip-calc-jsonld');
    document.head.appendChild(script);
    return () => { script.remove(); };
  }, []);

  return (
    <div className="mt-10" data-testid="drip-calc-faq">
      <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100 mb-3">Frequently asked</h2>
      <div className="space-y-3">
        {FAQS.map((f, i) => (
          <details key={i} className="p-4 rounded-lg border border-slate-200 dark:border-slate-800 group" data-testid={`drip-calc-faq-item-${i}`}>
            <summary className="cursor-pointer font-semibold text-slate-900 dark:text-slate-100 list-none flex items-start justify-between gap-3">
              <span>{f.q}</span>
              <ArrowRight className="w-4 h-4 mt-1 shrink-0 text-slate-400 group-open:rotate-90 transition-transform" />
            </summary>
            <p className="mt-3 text-sm text-slate-700 dark:text-slate-300 leading-relaxed">{f.a}</p>
          </details>
        ))}
      </div>
    </div>
  );
}
